import { BACKEND_KEYS } from '../../common/consts/app-keys.const';
import {
  HttpFactoryService,
  EnhancedWithAuthHttpService
} from '../../common/services';
import {
  IErrorsMetricData,
  IRpmMetricData,
  IResponseTimeMetricData,
  IRevenueMetricData
} from '../../common/types/metrics.types';

export interface IAdminLicenseKey {
  id: number;
  springId: string;
  key: string;
  type: string;
  expiresAt: string;
  maxActivations: number;
  product: { name: string } | null;
}

// A licence from Squarebit's own licence server. Distinct from
// IAdminLicenseKey above, which is a LicenseSpring row.
export interface IAdminLicenseActivation {
  id: number;
  machineLabel: string | null;
  os: string | null;
  hostApp: string | null;
  appVersion: string | null;
  seatClass: string;
  activatedAt: string;
  lastSeenAt: string;
  releasedAt: string | null;
  // online | offline. Display and alerting only — never the seat count.
  activationMode: string;
  // When an offline .lic stops verifying; the exposure window after a
  // revocation, since revocation cannot reach an air-gapped machine.
  tokenExpiresAt: string | null;
}

export interface IAdminProductLicense {
  id: number;
  productSlug: string;
  key: string;
  seats: number;
  usedSeats: number;
  status: string;
  statusReason: string | null;
  source: string;
  expiresAt: string | null;
  createdAt: string;
  activations: IAdminLicenseActivation[];
}

export interface ILoginEvent {
  id: number;
  createdAt: string;
  ipAddress: string | null;
  userAgent: string | null;
  success: boolean;
}

export interface IAdminUser {
  id: number;
  email: string;
  createdAt: string;
  isActivated: boolean;
  role: string;
  trialAvailable: boolean;
  adminNotes: string | null;
  lastLoginAt: string | null;
  failedAttempts: number;
  isLocked: boolean;
  // Whether the account holds the admin-granted Squarebit Eye full-package entitlement.
  /** Every product slug this account has been granted. */
  grants?: string[];
  /** Derived alias for the Eye grant, kept while the UI moves to `grants`. */
  eyeAccess: boolean;
  /**
   * Seats across this account's non-revoked Eye licences, or null when it
   * holds none. Null and 0 are different facts, and the table renders the
   * first as a dash rather than a zero.
   */
  eyeSeats?: number | null;
  License_key: IAdminLicenseKey[];
}

export interface IAssetDownloadStat {
  id: number;
  slug: string;
  name: string;
  category: string;
  downloads: number;
}

export interface IDownloadStats {
  totalDownloads: number;
  byAsset: IAssetDownloadStat[];
}

export interface ILicenseActivityEvent {
  id: number;
  createdAt: string;
  edition: string;
  product: string;
  event: string;
  result: string | null;
  licenseKey: string;
  hardwareId: string;
  pluginVersion: string | null;
  validUntil: string | null;
  seatsMax: number | null;
  seatsUsed: number | null;
}

export interface ILicenseActivityList {
  events: ILicenseActivityEvent[];
  total: number;
  page: number;
  pageSize: number;
}

export interface IEditionSummary {
  product: string;
  edition: string;
  activations: number;
  uniqueKeys: number;
  uniqueMachines: number;
  blockedAttempts: number;
  activationsEnabled: boolean;
  configUpdatedAt: string | null;
  configUpdatedBy: string | null;
}

export interface ILicenseActivityFilters {
  page?: number;
  pageSize?: number;
  product?: string;
  edition?: string;
  event?: string;
  result?: string;
  key?: string;
  from?: string;
  to?: string;
}

class AdminService {
  httpService: EnhancedWithAuthHttpService;

  constructor(private httpFactory: HttpFactoryService) {
    this.httpService = httpFactory.createAuthHttpService();
  }

  private filterDictData<T extends { date: string }>(
    data: IResponseTimeMetricData
  ): IResponseTimeMetricData {
    const filteredData: IResponseTimeMetricData = {};

    Object.keys(data).forEach((key) => {
      filteredData[key] = this.filterArrayData(data[key]);
    });

    return filteredData;
  }

  private filterArrayData<T extends { date: string }>(data: T[]): T[] {
    return data.map((item) => ({
      ...item,
      date: new Date(item.date).toLocaleDateString(navigator.language || 'en-US', {
        month: 'short',
        day: 'numeric'
      })
    }));
  }

  async getUsers(): Promise<{
    users: IAdminUser[];
    mrr: number;
    totalDownloads: number;
  }> {
    const response = await this.httpService.get<{
      users: IAdminUser[];
      mrr: number;
      totalDownloads: number;
    }>(BACKEND_KEYS.ADMIN_USERS);
    return response as { users: IAdminUser[]; mrr: number; totalDownloads: number };
  }

  async resetTrial(userId: number): Promise<void> {
    await this.httpService.post(
      `${BACKEND_KEYS.ADMIN_USERS}/${userId}/reset-trial`,
      {}
    );
  }

  async resendActivation(userId: number): Promise<void> {
    await this.httpService.post(
      `${BACKEND_KEYS.ADMIN_USERS}/${userId}/resend-activation`,
      {}
    );
  }

  async forceActivate(userId: number): Promise<void> {
    await this.httpService.post(
      `${BACKEND_KEYS.ADMIN_USERS}/${userId}/force-activate`,
      {}
    );
  }

  /**
   * Grant or revoke a product entitlement. The endpoint has always taken a
   * slug — one row per (user, product) — so a new gated product needs nothing
   * here beyond passing its slug.
   */
  async setProductAccess(
    userId: number,
    productSlug: string,
    grant: boolean,
    seats?: number
  ): Promise<void> {
    if (grant) {
      await this.httpService.post(
        `${BACKEND_KEYS.ADMIN_USERS}/${userId}/grant-access`,
        seats === undefined ? { productSlug } : { productSlug, seats }
      );
    } else {
      await this.httpService.delete(
        `${BACKEND_KEYS.ADMIN_USERS}/${userId}/grant-access?productSlug=${encodeURIComponent(productSlug)}`
      );
    }
  }

  // Grant or revoke the Squarebit Eye full-package download entitlement.
  async setEyeAccess(userId: number, grant: boolean): Promise<void> {
    return this.setProductAccess(userId, 'squarebit-eye', grant);
  }

  // --- Squarebit-issued licences (modules/licensing) ----------------------
  // These endpoints 404 while LICENSING_ENABLED is off, which reads as "this
  // account has no licences" rather than an error — the admin panel must not
  // show a failure for a feature that is simply switched off.

  async getUserLicenses(userId: number): Promise<IAdminProductLicense[]> {
    try {
      const response = await this.httpService.get<IAdminProductLicense[]>(
        `${BACKEND_KEYS.ADMIN_USERS}/${userId}/licenses`
      );
      return Array.isArray(response) ? response : [];
    } catch {
      return [];
    }
  }

  async issueLicense(
    userId: number,
    productSlug: string,
    seats: number
  ): Promise<{ id: number; key: string } | void> {
    return this.httpService.post(`${BACKEND_KEYS.ADMIN_USERS}/${userId}/licenses`, {
      productSlug,
      seats
    });
  }

  async updateLicense(
    licenseId: number,
    patch: { seats?: number; status?: string; expiresAt?: string | null }
  ): Promise<void> {
    await this.httpService.put(`${BACKEND_KEYS.ADMIN_LICENSES}/${licenseId}`, patch);
  }

  async releaseLicenseActivation(
    licenseId: number,
    activationId: number
  ): Promise<void> {
    await this.httpService.post(
      `${BACKEND_KEYS.ADMIN_LICENSES}/${licenseId}/activations/${activationId}/release`,
      {}
    );
  }

  async getLoginHistory(userId: number): Promise<ILoginEvent[]> {
    const response = await this.httpService.get<ILoginEvent[]>(
      `${BACKEND_KEYS.ADMIN_USERS}/${userId}/login-history`
    );
    return response ? (response as ILoginEvent[]) : [];
  }

  async updateNotes(userId: number, notes: string): Promise<void> {
    await this.httpService.put(`${BACKEND_KEYS.ADMIN_USERS}/${userId}/notes`, {
      notes
    });
  }

  async deleteUser(
    userId: number,
    emailUser: boolean = true
  ): Promise<{ ok: boolean; warnings: string[] }> {
    const response = await this.httpService.delete<{
      ok: boolean;
      warnings?: string[];
    }>(`${BACKEND_KEYS.ADMIN_USERS}/${userId}?emailUser=${emailUser}`);
    const data = (response ?? {}) as { ok?: boolean; warnings?: string[] };
    return { ok: data.ok ?? true, warnings: data.warnings ?? [] };
  }

  async getDownloadStats(): Promise<IDownloadStats> {
    const response = await this.httpService.get<IDownloadStats>(
      BACKEND_KEYS.DOWNLOAD_STATS
    );
    return response as IDownloadStats;
  }

  async getErrorsMetricData(days: number = 30): Promise<IErrorsMetricData[]> {
    const response = await this.httpService.get<IErrorsMetricData[]>(
      BACKEND_KEYS.METRICS_ERROR
    );
    const responseData = response ? (response as IErrorsMetricData[]) : [];

    return this.filterArrayData(responseData) as IErrorsMetricData[];
  }

  async getRpmData(days: number = 30): Promise<IRpmMetricData[]> {
    const response = await this.httpService.get<IRpmMetricData[]>(
      BACKEND_KEYS.METRICS_RPM
    );
    const responseData = response ? (response as IRpmMetricData[]) : [];

    return this.filterArrayData(responseData) as IRpmMetricData[];
  }

  async getResponseTimeData(days: number = 30): Promise<IResponseTimeMetricData> {
    const response = await this.httpService.get<IResponseTimeMetricData>(
      BACKEND_KEYS.METRICS_ART
    );
    const responseData = response ? (response as IResponseTimeMetricData) : {};

    return this.filterDictData(responseData) as IResponseTimeMetricData;
  }

  async getRevenueMetricData(days: number = 30): Promise<IRevenueMetricData[]> {
    const response = await this.httpService.get<IRevenueMetricData[]>(
      BACKEND_KEYS.METRICS_REVENUE
    );
    const responseData = response ? (response as IRevenueMetricData[]) : [];

    return this.filterArrayData(responseData) as IRevenueMetricData[];
  }

  async getLicenseActivity(
    filters: ILicenseActivityFilters
  ): Promise<ILicenseActivityList> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== undefined && v !== null && `${v}`.length > 0) {
        params.set(k, `${v}`);
      }
    });
    const query = params.toString();
    const response = await this.httpService.get<ILicenseActivityList>(
      `${BACKEND_KEYS.LICENSE_ACTIVITY}${query ? `?${query}` : ''}`
    );
    return (response ?? {
      events: [],
      total: 0,
      page: 1,
      pageSize: 50
    }) as ILicenseActivityList;
  }

  async getLicenseActivitySummary(): Promise<{ editions: IEditionSummary[] }> {
    const response = await this.httpService.get<{ editions: IEditionSummary[] }>(
      BACKEND_KEYS.LICENSE_ACTIVITY_SUMMARY
    );
    return (response ?? { editions: [] }) as { editions: IEditionSummary[] };
  }

  async setEditionActivations(
    product: string,
    edition: string,
    activationsEnabled: boolean
  ): Promise<void> {
    await this.httpService.put(
      `${BACKEND_KEYS.LICENSE_ACTIVITY_CONFIG}/${product}/${edition}`,
      { activationsEnabled }
    );
  }
  /**
   * Set how many machines a LicenseSpring licence may be activated on.
   *
   * The framework has no checkout to carry a seat quantity the way TouchPose
   * does, so this is where its activation limit is decided. Rejects with the
   * server's message when the new count is below what is already activated.
   */
  async setLicenseSeats(licenseId: number, seats: number): Promise<void> {
    await this.httpService.put(
      `${BACKEND_KEYS.ADMIN_LICENSE_SEATS}/${licenseId}/seats`,
      { seats }
    );
  }
  /**
   * Issue (or adopt) the framework licence for an account that already has the
   * grant. Idempotent, and the fix for an account granted while the licensing
   * config was incomplete.
   */
  async issueFrameworkLicense(userId: number): Promise<void> {
    await this.httpService.post(
      `${BACKEND_KEYS.ADMIN_USERS}/${userId}/issue-framework-license`,
      {}
    );
  }
  /**
   * Remove the framework licence so a new one can be issued in its place. The
   * grant is left alone: this replaces a key, it does not remove access.
   */
  async removeFrameworkLicense(userId: number): Promise<void> {
    await this.httpService.delete(
      `${BACKEND_KEYS.ADMIN_USERS}/${userId}/framework-license`
    );
  }
}

const adminService = new AdminService(new HttpFactoryService());

export { adminService };
